import React, { createContext, useContext, useState, useEffect } from "react";
import { AuthVerificationData } from "@/types/adminSettings";
import { DebugEntry } from "@/components/admin/settings/DebugConsole";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from 'uuid';
import { useVerificationSteps } from "@/hooks/useVerificationSteps";
import { SessionVerification, VerificationStep } from "@/types/verificationTypes";
import { isVerificationTrackingEnabled } from "@/services/VerificationService";
import { toast } from "@/hooks/use-toast";

const LOCAL_STORAGE_KEY = "AUTH_VERIFICATION_DATA";
const DEBUG_LOGS_KEY = "AUTH_DEBUG_LOGS";

type AuthVerificationContextType = {
  verificationData: AuthVerificationData | null;
  setVerificationData: (data: AuthVerificationData | null) => void;
  clearVerificationData: () => void;
  debugLogs: DebugEntry[];
  addDebugLog: (message: string, type?: "info" | "success" | "error" | "warning", details?: any) => void;
  clearDebugLogs: () => void;
  createTestAuthFlow: () => Promise<string | null>;
  createLiveAuthFlow: () => Promise<string | null>;
  recordVerificationStep: (step: string, status: "info" | "success" | "error" | "warning", details?: any, sessionIdOverride?: string) => Promise<boolean>;
  startVerificationSession: (userEmail?: string, type?: string, initialData?: Record<string, any>, label?: string) => Promise<string>;
  completeVerificationSession: (success: boolean, finalData?: Record<string, any>, sessionIdOverride?: string) => Promise<boolean>;
  getSessionById: (id: string) => Promise<SessionVerification | null>;
  getStepsBySessionId: (id: string) => Promise<VerificationStep[]>;
  activeSessionId: string | null;
};

const AuthVerificationContext = createContext<AuthVerificationContextType | undefined>(undefined);

export const AuthVerificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    sessionId: activeSessionId,
    startNewSession,
    recordStep,
    completeSession,
    getSessionById,
    getStepsBySessionId
  } = useVerificationSteps();
  
  const [verificationData, setVerificationDataState] = useState<AuthVerificationData | null>(() => {
    try {
      const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
      return storedData ? JSON.parse(storedData) : null;
    } catch (error) {
      console.error("Error reading verification data from localStorage:", error);
      return null;
    }
  });
  
  const [debugLogs, setDebugLogs] = useState<DebugEntry[]>(() => {
    try {
      const storedLogs = localStorage.getItem(DEBUG_LOGS_KEY);
      return storedLogs ? JSON.parse(storedLogs) : [];
    } catch (error) {
      console.error("Error reading debug logs from localStorage:", error);
      return [];
    }
  });
  
  useEffect(() => {
    try {
      if (verificationData) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(verificationData));
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Error saving verification data to localStorage:", error);
    }
  }, [verificationData]);
  
  useEffect(() => {
    try {
      localStorage.setItem(DEBUG_LOGS_KEY, JSON.stringify(debugLogs));
    } catch (error) {
      console.error("Error saving debug logs to localStorage:", error);
    }
  }, [debugLogs]);
  
  const startVerificationSession = async (
    userEmail?: string, 
    type?: string,
    initialData?: Record<string, any>,
    label?: string
  ): Promise<string> => {
    if (!isVerificationTrackingEnabled()) {
      console.log(`[DEBUG-startVerificationSession] Verification tracking is disabled, skipping session for ${userEmail || 'unknown user'}`);
      return "";
    }
    
    console.log(`[DEBUG-startVerificationSession] Starting session for ${userEmail || 'unknown user'}, type: ${type || 'unknown'}`);
    return startNewSession(userEmail, type, initialData, label);
  };
  
  const recordVerificationStep = async (
    step: string, 
    status: "info" | "success" | "error" | "warning" = "info", 
    details?: any,
    sessionIdOverride?: string
  ): Promise<boolean> => {
    if (!isVerificationTrackingEnabled()) {
      console.log(`[DEBUG-recordVerificationStep] Verification tracking is disabled, skipping step "${step}"`);
      return false;
    }
    
    const sessionId = sessionIdOverride || activeSessionId;
    
    addDebugLog(step, status, details);
    console.log(`[DEBUG-recordVerificationStep] Recording step "${step}" with status "${status}"${sessionId ? ` for session ${sessionId}` : ' (no session ID)'}`);
    
    if (!sessionId) {
      console.warn(`[DEBUG-recordVerificationStep] Warning: No session ID available for step "${step}"`);
      return false;
    }
    
    try {
      return await recordStep({
        step,
        status,
        details
      }, sessionId);
    } catch (error) {
      console.error(`[DEBUG-recordVerificationStep] Error recording step "${step}":`, error);
      return false;
    }
  };
  
  const completeVerificationSession = async (
    success: boolean,
    finalData?: Record<string, any>,
    sessionIdOverride?: string
  ): Promise<boolean> => {
    if (!isVerificationTrackingEnabled()) {
      console.log(`[DEBUG-completeVerificationSession] Verification tracking is disabled, skipping session completion`);
      return false;
    }
    
    const sessionId = sessionIdOverride || activeSessionId;
    
    if (!sessionId) {
      console.warn("[DEBUG-completeVerificationSession] No active session ID for completing session");
      return false;
    }
    
    console.log(`[DEBUG-completeVerificationSession] Completing session ${sessionId} with success=${success}`);
    return completeSession(success, finalData, sessionId);
  };
  
  const setVerificationData = (data: AuthVerificationData | null) => {
    setVerificationDataState(data);
    try {
      if (data) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Error directly saving verification data to localStorage:", error);
    }
  };
  
  const clearVerificationData = () => {
    setVerificationDataState(null);
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch (error) {
      console.error("Error removing verification data from localStorage:", error);
    }
  };
  
  const addDebugLog = (message: string, type: "info" | "success" | "error" | "warning" = "info", details?: any) => {
    const newLog: DebugEntry = {
      timestamp: new Date().toISOString(),
      message,
      type,
      details
    };
    
    setDebugLogs(prevLogs => {
      const updatedLogs = [newLog, ...prevLogs].slice(0, 100);
      try {
        localStorage.setItem(DEBUG_LOGS_KEY, JSON.stringify(updatedLogs));
      } catch (error) {
        console.error("Error saving debug logs to localStorage:", error);
      }
      return updatedLogs;
    });
  };
  
  const clearDebugLogs = () => {
    setDebugLogs([]);
    try {
      localStorage.removeItem(DEBUG_LOGS_KEY);
    } catch (error) {
      console.error("Error removing debug logs from localStorage:", error);
    }
  };
  
  const createTestAuthFlow = async (): Promise<string | null> => {
    if (!isVerificationTrackingEnabled()) {
      console.log(`[DEBUG-createTestAuthFlow] Verification tracking is disabled, skipping test auth flow`);
      toast({
        title: "Testing Mode Disabled",
        description: "Please enable testing mode in Admin Settings to use test auth flows."
      });
      return null;
    }
    
    try {
      const timestamp = new Date().getTime();
      const testEmail = `test-${timestamp}@example.com`;
      const displayName = `Test User ${timestamp}`;
      
      console.log("[DEBUG-createTestAuthFlow] Starting test auth flow with email:", testEmail);
      
      const newSessionId = await startNewSession(
        testEmail,
        'test_auth_flow',
        {
          email: testEmail,
          displayName,
          timestamp: new Date().toISOString()
        },
        'Account Set Up - mock URL. Create URL'
      );
      
      console.log("[DEBUG-createTestAuthFlow] Session created:", newSessionId);
      
      addDebugLog(`Creating test user with email: ${testEmail}`, "info");
      
      console.log("[DEBUG-createTestAuthFlow] Before recordStep call #1 (create_test_user)");
      const stepResult1 = await recordStep({
        step: 'create_test_user', 
        status: 'info',
        details: { email: testEmail, displayName }
      }, newSessionId);
      console.log("[DEBUG-createTestAuthFlow] After recordStep call #1, result:", stepResult1);
      
      const { data: userData, error: signUpError } = await supabase.auth.signUp({
        email: testEmail,
        password: `Test${timestamp}!`,
        options: {
          data: {
            display_name: displayName,
            role: 'auto_mock'
          }
        }
      });
      
      if (signUpError) {
        addDebugLog(`Error creating test user: ${signUpError.message}`, "error", signUpError);
        
        console.log("[DEBUG-createTestAuthFlow] Before recordStep call #2 (test_user_creation_failed)");
        const stepResult2 = await recordStep({
          step: 'test_user_creation_failed',
          status: 'error',
          details: signUpError
        }, newSessionId);
        console.log("[DEBUG-createTestAuthFlow] After recordStep call #2, result:", stepResult2);
        
        await completeSession(false, { error: signUpError }, newSessionId);
        return null;
      }
      
      addDebugLog(`Successfully created test user with ID: ${userData.user?.id}`, "success", {
        userId: userData.user?.id,
        email: testEmail,
        displayName,
        role: 'auto_mock'
      });
      
      console.log("[DEBUG-createTestAuthFlow] Before recordStep call #3 (test_user_created)");
      const stepResult3 = await recordStep({
        step: 'test_user_created',
        status: 'success',
        details: {
          userId: userData.user?.id,
          email: testEmail,
          displayName,
          role: 'auto_mock'
        }
      }, newSessionId);
      console.log("[DEBUG-createTestAuthFlow] After recordStep call #3, result:", stepResult3);
      
      const origin = window.location.origin;
      const mockVerificationUrl = `${origin}/auth?confirmation=demo&email=${encodeURIComponent(testEmail)}&sessionId=${newSessionId}`;
      
      addDebugLog(`Successfully generated mock verification URL for ${testEmail}`, "success", {
        verificationUrl: mockVerificationUrl,
        credentials: {
          email: testEmail,
          password: `Test${timestamp}!`,
          role: 'auto_mock'
        }
      });
      
      console.log("[DEBUG-createTestAuthFlow] Before recordStep call #4 (verification_url_generated)");
      const stepResult4 = await recordStep({
        step: 'verification_url_generated',
        status: 'success',
        details: {
          verificationUrl: mockVerificationUrl.substring(0, 50) + "...",
          credentials: {
            email: testEmail,
            password: `Test${timestamp}!`,
            role: 'auto_mock'
          }
        }
      }, newSessionId);
      console.log("[DEBUG-createTestAuthFlow] After recordStep call #4, result:", stepResult4);
      
      const testAuthData: AuthVerificationData = {
        sessionId: newSessionId,
        timestamp: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        url: "test-auth-flow",
        token: "",
        tokenVerified: false,
        user: {
          id: userData.user?.id || "",
          email: testEmail,
          displayName,
          role: 'auto_mock'
        },
        profileUpdated: false,
        sessionCreated: false,
        sessionEmail: "",
        sessionDisplayName: "",
        steps: [
          {
            step: 'test_auth_flow_initiated',
            timestamp: new Date().toISOString(),
            details: {
              email: testEmail,
              verificationUrl: mockVerificationUrl.substring(0, 50) + "...",
              role: 'auto_mock'
            }
          }
        ]
      };
      
      setVerificationData(testAuthData);
      
      console.log("[DEBUG-createTestAuthFlow] Auth flow completed successfully, returning URL:", mockVerificationUrl);
      return mockVerificationUrl;
    } catch (error: any) {
      console.error(`[DEBUG-createTestAuthFlow] Unexpected error: ${error.message}`, error);
      addDebugLog(`Unexpected error in test auth flow: ${error.message}`, "error", {
        error: error.message,
        stack: error.stack
      });
      
      if (activeSessionId) {
        console.log("[DEBUG-createTestAuthFlow] Before recordStep call #5 (unexpected_error)");
        const stepResult5 = await recordStep({
          step: 'unexpected_error',
          status: 'error',
          details: {
            error: error.message,
            stack: error.stack
          }
        }, activeSessionId);
        console.log("[DEBUG-createTestAuthFlow] After recordStep call #5, result:", stepResult5);
        
        await completeSession(false, {
          error: error.message,
          stack: error.stack
        }, activeSessionId);
      }
      
      return null;
    }
  };
  
  const createLiveAuthFlow = async (): Promise<string | null> => {
    if (!isVerificationTrackingEnabled()) {
      console.log(`[DEBUG-createLiveAuthFlow] Verification tracking is disabled, skipping live auth flow`);
      toast({
        title: "Testing Mode Disabled",
        description: "Please enable testing mode in Admin Settings to use live auth flows."
      });
      return null;
    }
    
    try {
      const testEmail = "20@lamulamu.com";
      const displayName = "Real URL";
      const testPassword = "lamulamu";
      
      console.log("[DEBUG-createLiveAuthFlow] Starting live auth flow with email:", testEmail);
      
      const newSessionId = await startNewSession(
        testEmail,
        'live_auth_flow',
        {
          email: testEmail,
          displayName,
          timestamp: new Date().toISOString()
        },
        'Account Set Up - live URL. Create Real Email'
      );
      
      console.log("[DEBUG-createLiveAuthFlow] Session created:", newSessionId);
      
      addDebugLog(`Creating live test user with email: ${testEmail}`, "info");
      
      await recordStep({
        step: 'create_live_test_user', 
        status: 'info',
        details: { email: testEmail, displayName }
      }, newSessionId);
      
      const { data: userData, error: signUpError } = await supabase.auth.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          data: {
            display_name: displayName,
            role: 'auto_live'
          }
        }
      });
      
      if (signUpError) {
        addDebugLog(`Error creating live test user: ${signUpError.message}`, "error", signUpError);
        
        await recordStep({
          step: 'live_test_user_creation_failed',
          status: 'error',
          details: signUpError
        }, newSessionId);
        
        await completeSession(false, { error: signUpError }, newSessionId);
        return null;
      }
      
      addDebugLog(`Successfully created live test user with ID: ${userData.user?.id}`, "success", {
        userId: userData.user?.id,
        email: testEmail,
        displayName,
        role: 'auto_live'
      });
      
      await recordStep({
        step: 'live_test_user_created',
        status: 'success',
        details: {
          userId: userData.user?.id,
          email: testEmail,
          displayName,
          role: 'auto_live'
        }
      }, newSessionId);
      
      addDebugLog(`A real verification email has been sent to ${testEmail}`, "success", {
        email: testEmail,
        password: testPassword
      });
      
      await recordStep({
        step: 'verification_email_sent',
        status: 'success',
        details: {
          email: testEmail,
          userId: userData.user?.id,
          message: "A real verification email has been automatically sent by Supabase"
        }
      }, newSessionId);
      
      const liveAuthData: AuthVerificationData = {
        sessionId: newSessionId,
        timestamp: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        url: "live-auth-flow",
        token: "",
        tokenVerified: false,
        user: {
          id: userData.user?.id || "",
          email: testEmail,
          displayName,
          role: 'auto_live'
        },
        profileUpdated: false,
        sessionCreated: false,
        sessionEmail: "",
        sessionDisplayName: "",
        steps: [
          {
            step: 'live_auth_flow_initiated',
            timestamp: new Date().toISOString(),
            details: {
              email: testEmail,
              role: 'auto_live'
            }
          }
        ]
      };
      
      setVerificationData(liveAuthData);
      
      console.log("[DEBUG-createLiveAuthFlow] Live auth flow completed successfully");
      return "Email sent to 20@lamulamu.com";
    } catch (error: any) {
      console.error(`[DEBUG-createLiveAuthFlow] Unexpected error: ${error.message}`, error);
      addDebugLog(`Unexpected error in live auth flow: ${error.message}`, "error", {
        error: error.message,
        stack: error.stack
      });
      
      if (activeSessionId) {
        await recordStep({
          step: 'live_auth_unexpected_error',
          status: 'error',
          details: {
            error: error.message,
            stack: error.stack
          }
        }, activeSessionId);
        
        await completeSession(false, {
          error: error.message,
          stack: error.stack
        }, activeSessionId);
      }
      
      return null;
    }
  };
  
  return (
    <AuthVerificationContext.Provider value={{ 
      verificationData, 
      setVerificationData, 
      clearVerificationData,
      debugLogs,
      addDebugLog,
      clearDebugLogs,
      createTestAuthFlow,
      createLiveAuthFlow,
      recordVerificationStep,
      startVerificationSession,
      completeVerificationSession,
      getSessionById,
      getStepsBySessionId,
      activeSessionId
    }}>
      {children}
    </AuthVerificationContext.Provider>
  );
};

export const useAuthVerificationContext = () => {
  const context = useContext(AuthVerificationContext);
  if (context === undefined) {
    console.warn("useAuthVerificationContext was called outside of its Provider");
    return {
      verificationData: null,
      setVerificationData: () => {
        console.warn("Attempted to set verification data outside of AuthVerificationProvider");
      },
      clearVerificationData: () => {
        console.warn("Attempted to clear verification data outside of AuthVerificationProvider");
      },
      debugLogs: [],
      addDebugLog: () => {
        console.warn("Attempted to add debug log outside of AuthVerificationProvider");
      },
      clearDebugLogs: () => {
        console.warn("Attempted to clear debug logs outside of AuthVerificationProvider");
      },
      createTestAuthFlow: async () => {
        console.warn("Attempted to create test auth flow outside of AuthVerificationProvider");
        return null;
      },
      createLiveAuthFlow: async () => {
        console.warn("Attempted to create live auth flow outside of AuthVerificationProvider");
        return null;
      },
      recordVerificationStep: async () => {
        console.warn("Attempted to record verification step outside of AuthVerificationProvider");
        return false;
      },
      startVerificationSession: async () => {
        console.warn("Attempted to start verification session outside of AuthVerificationProvider");
        return uuidv4();
      },
      completeVerificationSession: async () => {
        console.warn("Attempted to complete verification session outside of AuthVerificationProvider");
        return false;
      },
      getSessionById: async () => {
        console.warn("Attempted to get session by ID outside of AuthVerificationProvider");
        return null;
      },
      getStepsBySessionId: async () => {
        console.warn("Attempted to get steps by session ID outside of AuthVerificationProvider");
        return [];
      },
      activeSessionId: null
    };
  }
  return context;
};

export const getStoredVerificationData = (): AuthVerificationData | null => {
  try {
    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    return storedData ? JSON.parse(storedData) : null;
  } catch (error) {
    console.error("Error reading verification data from localStorage:", error);
    return null;
  }
};
